import { Link } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ArrowRight, GraduationCap, Users, BookOpen, CreditCard, BarChart3, Bell, Calendar, Shield, Clock, CheckCircle, Star, Smartphone, Globe } from "lucide-react";

const features = [
  {
    icon: Users,
    title: "Student Information System",
    description: "Centralized student database with comprehensive profiles, enrollment management, and academic history tracking.",
    details: ["Student profiles with photo & documents", "Enrollment & admission workflows", "Parent/guardian information management", "Student transfer & migration tools"],
  },
  {
    icon: BookOpen,
    title: "Academic Management",
    description: "Complete academic lifecycle management from curriculum planning to grade publishing.",
    details: ["Curriculum & syllabus management", "Class & section organization", "Subject allocation & timetable generation", "Academic year & term management"],
  },
  {
    icon: CreditCard,
    title: "Fee Management",
    description: "Streamlined fee collection, invoicing, and financial tracking with automated reminders.",
    details: ["Fee structure customization", "Online & offline payment tracking", "Automated receipt generation", "Overdue payment reminders & reports"],
  },
  {
    icon: BarChart3,
    title: "Exam & Results",
    description: "End-to-end examination management from scheduling to result publication and analysis.",
    details: ["Exam scheduling & seating plans", "Mark entry & grade computation", "Report card generation", "Performance analytics & comparisons"],
  },
  {
    icon: Bell,
    title: "Communication Hub",
    description: "Multi-channel communication between school, parents, and students.",
    details: ["Push notifications & announcements", "Parent-teacher messaging", "SMS & email integration", "Event notifications & reminders"],
  },
  {
    icon: Calendar,
    title: "Attendance Tracking",
    description: "Digital attendance management with real-time tracking and automated reporting.",
    details: ["Daily & period-wise attendance", "Biometric/QR code integration", "Absence notifications to parents", "Attendance analytics & reports"],
  },
];

const benefits = [
  { icon: Clock, title: "Save 60% Admin Time", desc: "Automate repetitive tasks and reduce paperwork significantly." },
  { icon: Shield, title: "Data Security", desc: "Enterprise-grade security with role-based access controls." },
  { icon: Smartphone, title: "Mobile First", desc: "Access everything from any device, anywhere, anytime." },
  { icon: Globe, title: "Cloud Hosted", desc: "No infrastructure worries — fully hosted and maintained by us." },
];

const pricing = [
  {
    name: "Starter",
    price: "Contact Us",
    desc: "Perfect for small schools getting started with digital management.",
    features: ["Up to 500 students", "Core modules (SIS, Attendance, Fees)", "Email support", "Monthly backups"],
    highlight: false,
  },
  {
    name: "Professional",
    price: "Contact Us",
    desc: "Ideal for growing institutions needing comprehensive tools.",
    features: ["Up to 2,000 students", "All modules included", "Priority support", "Daily backups", "Custom branding", "API access"],
    highlight: true,
  },
  {
    name: "Enterprise",
    price: "Contact Us",
    desc: "For large institutions and school groups with custom needs.",
    features: ["Unlimited students", "Multi-campus support", "Dedicated account manager", "Real-time backups", "Custom integrations", "On-premise option"],
    highlight: false,
  },
];

export default function Vidyora() {
  return (
    <div data-testid="vidyora-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-transparent to-orange-500/5" />
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="mb-4 bg-orange-500/10 text-orange-400 border-orange-500/20 hover:bg-orange-500/10 text-xs font-mono" data-testid="vidyora-badge">
                SCHOOL MANAGEMENT SYSTEM
              </Badge>
              <div className="flex items-center gap-3 mb-6">
                <GraduationCap className="w-10 h-10 text-orange-400" />
                <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
                  <span className="gradient-text-orange">Vidyora</span>
                </h1>
              </div>
              <p className="text-lg text-orange-300/80 font-medium mb-4">
                The Future of Education Management.
              </p>
              <p className="text-base text-slate-400 leading-relaxed mb-8 max-w-lg">
                Vidyora is a comprehensive, cloud-based school management system 
                that digitizes every aspect of school administration — from student 
                enrollment to exam results, fee collection to parent communication.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid="vidyora-cta-demo"
                  className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white px-8 py-3.5 rounded-full font-medium transition-all shadow-[0_0_20px_rgba(249,115,22,0.3)] hover:shadow-[0_0_30px_rgba(249,115,22,0.5)] hover:-translate-y-0.5"
                >
                  Request a Demo
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="#features"
                  data-testid="vidyora-cta-features"
                  className="inline-flex items-center gap-2 border border-orange-500/30 text-orange-300 hover:bg-orange-500/10 px-8 py-3.5 rounded-full font-medium transition-all hover:-translate-y-0.5"
                >
                  View Features
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden border border-orange-500/20 shadow-[0_0_60px_rgba(249,115,22,0.12)]">
                <img
                  src="https://images.pexels.com/photos/27141316/pexels-photo-27141316.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Vidyora Dashboard"
                  className="w-full h-80 object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 glass rounded-xl p-4 shadow-xl border border-orange-500/20">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                    <Star className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <div className="font-['Outfit'] font-semibold text-slate-100 text-sm">Trusted by Schools</div>
                    <div className="text-slate-400 text-xs">Across Telangana</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="relative py-24 lg:py-32" data-testid="vidyora-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-orange-500/10 text-orange-400 border-orange-500/20 hover:bg-orange-500/10 text-xs font-mono">
              FEATURES
            </Badge>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
              Everything your school <span className="gradient-text-orange">needs.</span>
            </h2>
            <p className="mt-4 text-base text-slate-400 max-w-lg mx-auto">
              Six powerful modules that work together to simplify school management.
            </p>
          </div>

          <Tabs defaultValue="0" className="w-full" data-testid="features-tabs">
            <TabsList className="flex flex-wrap justify-center gap-2 bg-transparent mb-12 h-auto">
              {features.map((f, i) => (
                <TabsTrigger
                  key={i}
                  value={String(i)}
                  data-testid={`feature-tab-${i}`}
                  className="px-4 py-2.5 rounded-xl bg-slate-900/50 border border-slate-800 text-slate-400 data-[state=active]:bg-orange-500/10 data-[state=active]:border-orange-500/30 data-[state=active]:text-orange-400 text-sm font-medium transition-all"
                >
                  <f.icon className="w-4 h-4 mr-2 inline" />
                  {f.title.split(' ').slice(0, 2).join(' ')}
                </TabsTrigger>
              ))}
            </TabsList>

            {features.map((f, i) => (
              <TabsContent key={i} value={String(i)} data-testid={`feature-content-${i}`}>
                <div className="grid lg:grid-cols-2 gap-8 p-8 rounded-2xl bg-slate-900/50 border border-slate-800">
                  <div>
                    <div className="w-14 h-14 rounded-xl bg-orange-500/10 flex items-center justify-center mb-6">
                      <f.icon className="w-7 h-7 text-orange-400" />
                    </div>
                    <h3 className="font-['Outfit'] text-2xl font-bold text-slate-50 mb-4">{f.title}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{f.description}</p>
                  </div>
                  <div>
                    <h4 className="font-['Outfit'] text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">
                      Key Capabilities
                    </h4>
                    <ul className="space-y-3">
                      {f.details.map((d, di) => (
                        <li key={di} className="flex items-start gap-3">
                          <CheckCircle className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-300 text-sm">{d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Benefits */}
      <section className="relative py-24 lg:py-32 bg-slate-900/30" data-testid="vidyora-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
              Why schools choose <span className="gradient-text-orange">Vidyora.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, i) => (
              <div key={i} data-testid={`benefit-${i}`} className="p-6 rounded-xl bg-slate-900/50 border border-slate-800 text-center hover:border-orange-500/20 transition-all group">
                <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-4 mx-auto group-hover:bg-orange-500/20 transition-colors">
                  <b.icon className="w-6 h-6 text-orange-400" />
                </div>
                <h3 className="font-['Outfit'] font-semibold text-slate-200 mb-2">{b.title}</h3>
                <p className="text-slate-400 text-xs leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="relative py-24 lg:py-32" data-testid="vidyora-pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-orange-500/10 text-orange-400 border-orange-500/20 hover:bg-orange-500/10 text-xs font-mono">
              PRICING
            </Badge>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
              Plans for every <span className="gradient-text-orange">school.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {pricing.map((plan, i) => (
              <div
                key={i}
                data-testid={`pricing-plan-${i}`}
                className={`p-8 rounded-2xl border transition-all ${
                  plan.highlight
                    ? "bg-orange-500/5 border-orange-500/30 shadow-[0_0_40px_rgba(249,115,22,0.1)]"
                    : "bg-slate-900/50 border-slate-800 hover:border-orange-500/20"
                }`}
              >
                {plan.highlight && (
                  <Badge className="mb-4 bg-orange-500/20 text-orange-400 border-orange-500/30 text-xs">
                    MOST POPULAR
                  </Badge>
                )}
                <h3 className="font-['Outfit'] text-xl font-bold text-slate-50 mb-1">{plan.name}</h3>
                <div className="font-['Outfit'] text-2xl font-bold text-orange-400 mb-3">{plan.price}</div>
                <p className="text-slate-400 text-sm mb-6">{plan.desc}</p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f, fi) => (
                    <li key={fi} className="flex items-start gap-3">
                      <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${plan.highlight ? "text-orange-400" : "text-slate-500"}`} />
                      <span className="text-slate-300 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  data-testid={`pricing-cta-${i}`}
                  className={`block text-center px-6 py-3 rounded-full font-medium transition-all text-sm ${
                    plan.highlight
                      ? "bg-orange-500 hover:bg-orange-400 text-white shadow-[0_0_20px_rgba(249,115,22,0.3)]"
                      : "bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700"
                  }`}
                >
                  Get Started
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 lg:py-32">
        <div className="absolute inset-0 bg-gradient-to-t from-orange-500/5 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
            Ready to modernize your <span className="gradient-text-orange">school?</span>
          </h2>
          <p className="mt-4 text-base text-slate-400 max-w-lg mx-auto">
            Schedule a free demo and see how Vidyora can transform your institution's management.
          </p>
          <Link
            to="/contact"
            data-testid="vidyora-final-cta"
            className="mt-8 inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white px-8 py-3.5 rounded-full font-medium transition-all shadow-[0_0_20px_rgba(249,115,22,0.3)] hover:shadow-[0_0_30px_rgba(249,115,22,0.5)] hover:-translate-y-0.5"
          >
            Request a Demo
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
